import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getEvidence } from "@/data/evidence";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { SectionShell, CTAPrimary } from "@/components/primitives";
import { useSEO } from "@/hooks/useSEO";

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-6 border-b hairline">
      <dt className="overline text-navy/45">{label}</dt>
      <dd className="sm:col-span-2 text-ink/80 font-body md:text-lg leading-relaxed">{value}</dd>
    </div>
  );
}

export default function EvidenceDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const rec = getEvidence(slug);
    if (rec) { setR(rec); setNotFound(false); }
    else { setR(null); setNotFound(true); }
  }, [slug]);

  useSEO(r ? `${r.org} — Client Evidence — HM Beacon` : null, r?.outcome);

  if (notFound) return (
    <div className="pt-40 pb-40 text-center" data-testid="evidence-not-found">
      <p className="font-heading font-light text-navy text-4xl">Evidence record not found.</p>
      <Link to="/evidence" className="mt-8 inline-block link-underline text-navy">Back to Client Evidence</Link>
    </div>
  );
  if (!r) return <div className="pt-40 pb-40 text-center text-ink/40 font-body">Loading…</div>;

  return (
    <div data-testid="evidence-detail-page">
      <header className="pt-40 md:pt-52 pb-14 border-b hairline">
        <SectionShell>
          <Reveal>
            <Link to="/evidence" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-colors mb-10" data-testid="evidence-back-link">
              <ArrowLeft size={16} /> Client Evidence
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="flex items-center gap-4 mb-8">
              <span className="overline text-gold">{r.engagement_type}</span>
              {r.platform && (<><span className="w-8 h-px bg-navy/20" /><span className="text-xs uppercase tracking-widest text-navy/50">{r.platform}</span></>)}
            </div>
          </Reveal>
          <h1 className="font-heading font-light text-navy tracking-tight text-[2.4rem] leading-[1.05] md:text-6xl max-w-[18ch]">
            <MaskedLines lines={[r.org]} delay={0.1} />
          </h1>
          {r.person && <Reveal delay={0.25}><p className="mt-6 text-xl text-ink/70 font-body">{r.person} — {r.role}</p></Reveal>}
        </SectionShell>
      </header>

      <SectionShell className="py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <dl className="border-t hairline" data-testid="evidence-fields">
              <Row label="What HM Beacon Supported" value={r.supported} />
              <Row label="Platform" value={r.platform} />
              <Row label="Outcome" value={r.outcome} />
              <Row label="Evidence" value={r.evidence_type} />
              <Row label="Public Attribution" value={r.attribution ? "Permission on record" : "Not publicly attributed"} />
            </dl>
            <div className="mt-10 flex flex-wrap gap-4">
              {r.evidence_url && (
                <a href={r.evidence_url} target="_blank" rel="noreferrer" data-testid="evidence-published-link"
                  className="group inline-flex items-center gap-3 border border-navy text-navy px-7 py-4 text-sm hover:bg-navy hover:text-warm transition-colors duration-300">
                  View Published Evidence
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              )}
              {r.image && (
                <a href={r.image} target="_blank" rel="noreferrer" data-testid="evidence-image-link"
                  className="group inline-flex items-center gap-3 border border-navy/30 text-navy px-7 py-4 text-sm hover:border-navy transition-colors duration-300">
                  View Evidence
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              )}
            </div>
          </div>
          <div className="lg:col-span-5">
            {r.image ? (
              <Reveal>
                <div className="relative overflow-hidden bg-navy/5 border hairline">
                  <img src={r.image} alt={`${r.org} — ${r.platform}`} className="w-full h-auto object-cover" data-testid="evidence-image" />
                </div>
              </Reveal>
            ) : (
              <div className="border hairline bg-mist/40 aspect-[4/3] flex items-center justify-center px-8 text-center">
                <span className="text-navy/30 text-sm uppercase tracking-[0.2em] font-body">Documented on record</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 pt-10 border-t hairline">
          <CTAPrimary to="/contact" testId="evidence-cta">Map Your Visibility</CTAPrimary>
        </div>
      </SectionShell>
    </div>
  );
}
